import React, { useEffect, useState } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, Card, CardBody, Flex, FormControl, FormLabel, Heading, Select, Text, Textarea, useToast, VStack,
} from '@chakra-ui/react';
import { TenantSettingsAPI } from '../services/api';
import BusinessHoursEditor, { formFieldsToHours, hoursToFormFields } from './BusinessHoursEditor';

// Company hours + after-hours behaviour (scenario gap G1, 2026-10-26). Saved into
// tenant_settings.business_hours / after_hours_policy; the chat widget's hours are mirrored by the API.
const COPY = {
  zh: {
    title: '营业时间与下班后', subtitle: '电话前台和网页客服共用。下班时，AI 会说明办公室已下班，并按下面的规则处理。',
    anyTime: '全天营业（不区分上下班）', open: '现在：营业中', closed: '现在：已下班', policy: '下班后来电', callback: '记录回电，不转接（推荐）',
    allowTransfer: '允许转接，无人接听再记录回电', greeting: '下班后开场白（可选，留空则自动生成）', greetingEn: '英文', greetingZh: '中文',
    save: '保存营业时间', saved: '营业时间已保存', failed: '保存失败', invalid: '营业时间填写不完整',
  },
  en: {
    title: 'Business hours & after-hours', subtitle: 'Shared by the phone front desk and website chat. When closed, the AI says so and follows the rule below.',
    anyTime: 'Open at any time (no after-hours)', open: 'Now: open', closed: 'Now: closed', policy: 'After-hours calls', callback: 'Take a callback, no transfers (recommended)',
    allowTransfer: 'Allow transfers; take a callback if nobody answers', greeting: 'After-hours greeting (optional; generated when empty)', greetingEn: 'English', greetingZh: 'Chinese',
    save: 'Save business hours', saved: 'Business hours saved', failed: 'Save failed', invalid: 'The hours are incomplete',
  },
};

const SLA_PRESETS = ['same_business_day', 'next_business_day', 'two_business_days', 'within_one_week'];
const SLA_COPY = {
  zh: { label: '回电承诺（AI 对来电者说的话）', same_business_day: '本营业日内回电，下班后顺延下一营业日', next_business_day: '下一个工作日结束前回电', two_business_days: '两个工作日内回电（默认）', within_one_week: '七个自然日内回电', note: '当天承诺在下班后顺延至下一营业日结束前。一周按商家时区的七个自然日计算。暂不支持自定义时限；历史自定义内容保留但不生效。' },
  en: { label: 'Callback promise (what the AI tells callers)', same_business_day: 'This business day; after closing, the next business day', next_business_day: 'By the end of the next business day', two_business_days: 'Within two business days (default)', within_one_week: 'Within seven calendar days', note: 'After closing, same-day callbacks move to the end of the next business day. A week is seven calendar days in the company timezone. Custom deadlines are not available; legacy custom text is retained but inactive.' },
};

export default function BusinessHoursCard({ tenantId, lang = 'en', initialHours, initialPolicy, openNow, timezone, initialSla }) {
  const copy = COPY[lang === 'zh' ? 'zh' : 'en'];
  const toast = useToast();
  const [form, setForm] = useState(() => hoursToFormFields(initialHours, timezone));
  const [action, setAction] = useState('callback');
  const [greetingEn, setGreetingEn] = useState('');
  const [greetingZh, setGreetingZh] = useState('');
  const [slaPreset, setSlaPreset] = useState('two_business_days');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(hoursToFormFields(initialHours, timezone));
    setAction(initialPolicy?.action === 'allow_transfer' ? 'allow_transfer' : 'callback');
    setGreetingEn(initialPolicy?.greeting?.en || '');
    setGreetingZh(initialPolicy?.greeting?.zh || '');
    setSlaPreset(SLA_PRESETS.includes(initialSla?.preset) ? initialSla.preset : 'two_business_days');
  }, [initialHours, initialPolicy, initialSla, timezone]);

  const hours = formFieldsToHours(form);
  const invalid = hours === undefined;

  const save = async () => {
    if (!tenantId || invalid) return;
    setSaving(true);
    try {
      const greeting = {};
      if (greetingEn.trim()) greeting.en = greetingEn.trim().slice(0, 500);
      if (greetingZh.trim()) greeting.zh = greetingZh.trim().slice(0, 500);
      await TenantSettingsAPI.update(tenantId, {
        ...(hours === null ? { clear_business_hours: true } : { business_hours: hours }),
        after_hours_policy: { action, ...(Object.keys(greeting).length ? { greeting } : {}) },
        callback_sla: { preset: slaPreset },
      });
      toast({ title: copy.saved, status: 'success', duration: 2000, isClosable: true });
    } catch (error) {
      toast({ title: copy.failed, description: error?.response?.data?.detail?.toString?.() || '', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card mb={6} borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.200" data-testid="business-hours-card">
      <CardBody py={6}>
        <VStack align="stretch" spacing={4}>
          <Flex justify="space-between" align="center" gap={3} flexWrap="wrap">
            <Box>
              <Heading size="md">{copy.title}</Heading>
              <Text fontSize="sm" color="gray.600">{copy.subtitle}</Text>
            </Box>
            {initialHours && typeof openNow === 'boolean' && (
              <Badge colorScheme={openNow ? 'green' : 'gray'}>{openNow ? copy.open : copy.closed}</Badge>
            )}
          </Flex>
          <BusinessHoursEditor form={form} onChange={setForm} lang={lang} anyTimeLabel={copy.anyTime} />
          <FormControl maxW="420px">
            <FormLabel fontSize="sm">{copy.policy}</FormLabel>
            <Select size="sm" value={action} onChange={(e) => setAction(e.target.value)} isDisabled={form.anyTime}>
              <option value="callback">{copy.callback}</option>
              <option value="allow_transfer">{copy.allowTransfer}</option>
            </Select>
          </FormControl>
          {!form.anyTime && (
            <FormControl>
              <FormLabel fontSize="sm">{copy.greeting}</FormLabel>
              <Text fontSize="xs" color="gray.500" mb={1}>{copy.greetingEn}</Text>
              <Textarea size="sm" rows={2} maxLength={500} value={greetingEn} onChange={(e) => setGreetingEn(e.target.value)} />
              <Text fontSize="xs" color="gray.500" mt={2} mb={1}>{copy.greetingZh}</Text>
              <Textarea size="sm" rows={2} maxLength={500} value={greetingZh} onChange={(e) => setGreetingZh(e.target.value)} />
            </FormControl>
          )}
          <FormControl maxW="420px">
            <FormLabel fontSize="sm">{SLA_COPY[lang === 'zh' ? 'zh' : 'en'].label}</FormLabel>
            <Select size="sm" value={slaPreset} onChange={(e) => setSlaPreset(e.target.value)} data-testid="callback-sla">
              {SLA_PRESETS.map((p) => <option key={p} value={p}>{SLA_COPY[lang === 'zh' ? 'zh' : 'en'][p]}</option>)}
            </Select>
            <Text fontSize="xs" color="gray.500" mt={1}>{SLA_COPY[lang === 'zh' ? 'zh' : 'en'].note}</Text>
          </FormControl>
          {invalid && <Alert status="warning" fontSize="sm"><AlertIcon />{copy.invalid}</Alert>}
          <Flex justify="flex-end">
            <Button size="sm" colorScheme="blue" onClick={save} isLoading={saving} isDisabled={invalid}>{copy.save}</Button>
          </Flex>
        </VStack>
      </CardBody>
    </Card>
  );
}
